import { formatUnitValue, unitLabel } from '../../units/unitConversions';
const COMPLETE_PATTERN = /^\s*Ø?\s*(\d+(?:[.,]\d+)?)\s*([A-Za-z]+)(\d+)\s*$/;
const INCOMPLETE_PATTERN = /^\s*Ø?\s*(?:\d+(?:[.,]\d*)?\s*(?:[A-Za-z]+)?)?\s*$/;
const EDITABLE_PATTERN = /^\s*Ø?\s*(\d+(?:[.,]\d+)?)\s*([A-Za-z]+)(\d*)\s*$/;

export const parseToleranceInput = (value) => {
  const input = value ?? '';

  if (input.trim() === '') {
    return { status: 'empty' };
  }

  const match = input.match(COMPLETE_PATTERN);

  if (!match) {
    return INCOMPLETE_PATTERN.test(input)
      ? { status: 'incomplete' }
      : { status: 'invalid', message: 'Enter a value such as 25H7.' };
  }

  const [, diameterText, position, gradeText] = match;
  const diameter = Number(diameterText.replace(',', '.'));
  const isUppercase = position === position.toUpperCase();
  const isLowercase = position === position.toLowerCase();

  if (!isUppercase && !isLowercase) {
    return {
      status: 'invalid',
      message: 'Use uppercase for a hole or lowercase for a shaft.',
    };
  }

  if (!Number.isFinite(diameter) || diameter < 1 || diameter > 500) {
    return {
      status: 'invalid',
      message: 'Nominal size must be between 1 and 500 mm.',
    };
  }

  const grade = Number(gradeText);

  if (!Number.isSafeInteger(grade)) {
    return { status: 'invalid', message: 'Enter a valid IT grade.' };
  }

  return {
    status: 'valid',
    diameter,
    position,
    grade,
    type: isUppercase ? 'hole' : 'shaft',
  };
};

export const setToleranceInputType = (value, type) => {
  const match = (value ?? '').match(EDITABLE_PATTERN);

  if (!match) return value;

  const [, diameterText, position, gradeText] = match;
  const adjustedPosition = type === 'hole'
    ? position.toUpperCase()
    : position.toLowerCase();

  return `${diameterText}${adjustedPosition}${gradeText}`;
};

const requiresFourDecimals = (value) =>
  Math.abs(value * 1000 - Math.round(value * 1000)) > 1e-8;

export const getResultPrecision = (result) => {
  const relevantValues = [
    result.deviations.lowerMm,
    result.deviations.upperMm,
  ];

  return relevantValues.some(requiresFourDecimals) ? 4 : 3;
};

export const formatDimension = (value, precision, unitSystem = 'metric') =>
  unitSystem === 'inch' ? formatUnitValue(value, 'mm', unitSystem) : Number(value).toFixed(precision);

export const formatDeviation = (value, unitSystem = 'metric') => {
  if (unitSystem === 'inch') return `${formatUnitValue(value, 'um', unitSystem, { signed: true })} ${unitLabel('um', unitSystem)}`;
  const normalizedValue = Object.is(value, -0) ? 0 : Number(value);

  if (normalizedValue === 0) return '0 µm';
  return `${normalizedValue > 0 ? '+' : ''}${normalizedValue} µm`;
};

export const formatDesignation = (result, precision) =>
  `${formatDimension(result.nominalSizeMm, precision)}${result.position}${result.grade}`;
